import { motion, Variants } from "framer-motion";
import { ShieldCheck, History, MapPin, Ban } from "lucide-react";
import { handwritten } from "./editorial";

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
    },
  },
};

const items = [
  {
    numeral: "i",
    icon: ShieldCheck,
    title: "Identity, not handles",
    body: "Real first name, real photo. No anonymous accounts, no handle-driven cosplay. You see who you're looping in with before you commit.",
  },
  {
    numeral: "ii",
    icon: History,
    title: "History you can see",
    body: "Tap any host and see how many loops they've shown up to, how many they've hosted, and how often attendees came back. Trust through accountability.",
  },
  {
    numeral: "iii",
    icon: MapPin,
    title: "Your space is yours",
    body: "Privacy zones around your home and work. We never show your exact location to anyone but you: neighbourhood-level only.",
  },
  {
    numeral: "iv",
    icon: Ban,
    title: "One tap to mute, block, report",
    body: "If someone's off, you never have to see them again. One tap, done. Per-loop filters (age range, friends-of-friends etc.) are organiser-set, shown before you join.",
  },
];

export default function TrustMoat() {
  return (
    <section
      className="w-full py-24 md:py-36 px-6 md:px-12 overflow-hidden"
      style={{ backgroundColor: "var(--color-paper-tan)" }}
    >
      <div className="max-w-6xl w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-14 max-w-3xl"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-salmon whitespace-nowrap">
              03: The trust layer
            </span>
            <span className="flex-1 h-px bg-ink/15" />
          </div>
          <p
            className="text-2xl md:text-3xl text-salmon -rotate-1 mb-2"
            style={handwritten}
          >
            non-negotiable
          </p>
          <h2 className="text-4xl md:text-6xl font-serif tracking-tight mb-5 leading-[1.05]">
            Identity is the
            <br />
            <span className="italic text-salmon">trust layer.</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-700 max-w-2xl leading-relaxed">
            Spontaneous meetups only work if the people on the other side are
            real and accountable. Four primitives make that the default, not the
            exception.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
        >
          {items.map(({ numeral, icon: Icon, title, body }) => (
            <motion.div
              key={title}
              variants={itemVariants}
              whileHover={{ y: -6 }}
              className="relative bg-canvas border-2 border-ink/10 rounded-3xl p-8 md:p-10 flex flex-col items-start shadow-[6px_6px_0_0_rgba(5,5,5,0.06)] hover:shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] hover:border-ink transition-all duration-300 group"
            >
              <span
                className="absolute top-6 right-7 text-3xl text-ink/15 group-hover:text-salmon transition-colors"
                style={handwritten}
              >
                {numeral}
              </span>
              <div className="bg-salmon border border-ink/15 p-4 rounded-2xl mb-6 group-hover:-rotate-6 transition-transform">
                <Icon className="w-8 h-8 text-ink" />
              </div>
              <h3 className="text-2xl font-serif tracking-tight text-ink mb-3">
                {title}
              </h3>
              <p className="text-gray-700 leading-relaxed">{body}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
